// The Overview dashboard (a built-in tab beside Classic, see DashboardWidgets.js): one strip with
// the numbers that matter, the accounts with their trend and, for prop accounts, where they sit
// between the max loss and the target; one chart with three views; the biggest leak with a rule
// to add; the P&L calendar; and the best, worst and waiting trades. Picking one prop account turns
// the top into that account's pass and max-loss panel.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import Calendar from './Calendar';
import { DailyBars } from './Dashboard';
import { accountStatus } from './balance';
import { dayNets } from './dashboardMetrics';
import { netPnl, tradingDayKey, usd } from './pnl';
import { performanceStats, topInsight, unjournaledTrades } from './stats';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { addRoutineRule } from './routineRules';
import { ScoreBar } from './ScoreViews';

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const money = (n) => `${n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const pct = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(1)}%`;
const dayShort = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
const monthShort = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short' });
export const isPropAccount = (a) => Boolean(a && (a.type === 'Evaluation' || a.type === 'Funded' || a.profitTarget || a.maxLossLimit));

// The numbers strip.
function Numbers({ trades, accounts }) {
  const { t } = useI18n();
  const s = performanceStats(trades);
  const wins = trades.filter((x) => netPnl(x) > 0).length;
  const losses = trades.filter((x) => netPnl(x) < 0).length;
  const C = 2 * Math.PI * 14;
  const pf = s.profitFactor;
  const ratio = s.avgWin && s.avgLoss ? s.avgWin / s.avgLoss : null;
  return (
    <section className="dov-card dov-strip">
      <div className="dov-cell dov-net">
        <span className="dov-label">{t('Net P&L · after fees')}</span>
        <strong className={`dov-big ${tone(s.net)}`}>{money(s.net)}</strong>
        <span className="dov-muted">{t(trades.length === 1 ? '{n} trade' : '{n} trades', { n: trades.length })}</span>
        <div className="dov-mini">
          <span><small>{t('Today')}</small><b className={tone(s.today)}>{money(s.today)}</b></span>
          <span><small>{t('This week')}</small><b className={tone(s.week)}>{money(s.week)}</b></span>
        </div>
      </div>
      <div className="dov-cell">
        <span className="dov-label">{t('Win rate')}</span>
        <div className="dov-row">
          <strong className="dov-mid">{s.winRate === null ? '—' : `${Math.round(s.winRate)}%`}</strong>
          <svg className="dov-ring" viewBox="0 0 36 36" aria-hidden="true"><circle cx="18" cy="18" r="14" /><circle cx="18" cy="18" r="14" className="on" strokeDasharray={`${((s.winRate || 0) / 100) * C} ${C}`} /></svg>
        </div>
        <span className="dov-muted">{t('{w} wins · {l} losses', { w: wins, l: losses })}</span>
      </div>
      <div className="dov-cell">
        <span className="dov-label">{t('Profit factor')}</span>
        <div className="dov-row">
          <strong className={`dov-mid ${pf === null ? '' : pf >= 1 ? 'gain' : 'loss'}`}>{pf === null ? '—' : pf === Infinity ? '∞' : pf.toFixed(2)}</strong>
          <svg className="dov-gauge" viewBox="0 0 70 40" aria-hidden="true">
            <path d="M6 36 A29 29 0 0 1 64 36" className="track" />
            {pf !== null && <path d="M6 36 A29 29 0 0 1 64 36" className={`fill ${pf >= 1 ? 'gain' : 'loss'}`} strokeDasharray={`${Math.min(1, (pf === Infinity ? 2 : pf) / 2) * 91} 91`} />}
            <line x1="35" y1="3" x2="35" y2="11" className="mark" />
          </svg>
        </div>
        <span className="dov-muted">{t('Break-even line is 1.00')}</span>
      </div>
      <div className="dov-cell">
        <span className="dov-label">{t('Average win vs average loss')}</span>
        <div className="dov-row dov-vs">
          <strong className="gain">{s.avgWin === null ? '—' : money(s.avgWin)}</strong><span className="dov-muted">{t('vs')}</span><strong className="loss">{s.avgLoss === null ? '—' : `−${usd.format(s.avgLoss)}`}</strong>
        </div>
        {ratio !== null && <div className="dov-split"><b style={{ flex: ratio }} /><i style={{ flex: 1 }} /></div>}
        <span className="dov-muted">{ratio === null ? t('Needs a win and a loss') : t('Wins are {x}× your losses', { x: ratio.toFixed(1) })}</span>
      </div>
    </section>
  );
}

// One account: balance, the last session's change, a trend line, and for prop accounts where the
// balance sits between the max loss line and the profit target.
function AccountCard({ account, trades, positions = [], onOpen }) {
  const { t } = useI18n();
  const mine = trades.filter((x) => String(x.accountId) === String(account._id));
  const status = account.size ? accountStatus(account, mine) : null;
  const days = dayNets(mine);
  const last = days.length ? days[days.length - 1] : null;
  const balance = status ? status.balance : days.reduce((s, d) => s + d.net, 0);
  const prop = isPropAccount(account);
  // The balance (or running P&L) at the end of each of the last 20 trading days.
  let run = status ? balance - days.reduce((s, d) => s + d.net, 0) : 0;
  const points = days.map((d) => (run += d.net)).slice(-20);
  const lo = Math.min(...points);
  const hi = Math.max(...points);
  const line = points.map((v, i) => `${(i / Math.max(1, points.length - 1)) * 90},${30 - ((v - lo) / Math.max(1, hi - lo)) * 26}`).join(' ');
  const floor = status && status.toBlow !== undefined ? balance - status.toBlow : null;
  const target = status && status.target;
  const where = floor !== null && target ? Math.max(0, Math.min(100, ((balance - floor) / (target - floor)) * 100)) : null;
  const watch = status && (status.risk === 'danger' || status.risk === 'warning' || status.consistencyOk === false);
  return (
    <button type="button" className={`dov-card dov-acct${watch ? ' watch' : ''}`} onClick={onOpen}>
      <span className="dov-acct-head">
        <span className={`dov-dot ${watch ? 'watch' : prop ? 'prop' : 'personal'}`} aria-hidden="true" />
        <strong>{account.name}</strong>
        <span className={`dov-tag ${watch ? 'watch' : prop ? 'prop' : 'personal'}`}>{watch ? t('Watch') : t(prop ? 'Prop' : 'Personal')}</span>
      </span>
      <span className="dov-acct-body">
        {positions.length > 0 && !status ? (
          <span>
            <b className="dov-acct-balance">{t(positions.length === 1 ? '{n} open position' : '{n} open positions', { n: positions.length })}</b>
            <small className="dov-muted">{positions.slice(0, 2).map((p) => `${p.symbol} ${t(p.side).toLowerCase()} ${p.size} @ ${p.entryPrice}`).join(' · ')}{positions.some((p) => p.markPrice) ? ` · P&L ${signed(positions.reduce((s2, p) => s2 + (Number(p.unrealized) || 0), 0))}` : ''}</small>
          </span>
        ) : (
          <span>
            <b className="dov-acct-balance">{status ? money(balance) : signed(balance)}</b>
            {last && <small className={tone(last.net)}>{signed(last.net)}{status && balance ? ` (${pct((last.net / (balance - last.net)) * 100)})` : ''} · {dayShort(last.key)}</small>}
            {!status && !last && <small className="dov-muted">{t('Net P&L')}</small>}
            {positions.length > 0 && <small className="dov-muted">{t(positions.length === 1 ? '{n} open position' : '{n} open positions', { n: positions.length })}</small>}
          </span>
        )}
        {points.length > 1 && <svg className="dov-spark" viewBox="0 0 90 32" aria-hidden="true"><polyline points={line} className={tone(points[points.length - 1] - points[0])} /></svg>}
      </span>
      {where !== null && (
        <span className="dov-range">
          <span className="dov-range-track"><i style={{ left: `${where}%` }} /></span>
          <span className="dov-range-labels"><small className="loss">{t('{amount} from max loss', { amount: usd.format(Math.max(0, status.toBlow)) })}</small><small className="gain">{t('{amount} to pass', { amount: usd.format(Math.max(0, target - balance)) })}</small></span>
        </span>
      )}
    </button>
  );
}

// One prop account picked: its balance, what's left to pass, the max-loss room and consistency.
function PropPanel({ account, trades }) {
  const { t } = useI18n();
  const mine = trades.filter((x) => String(x.accountId) === String(account._id));
  const status = accountStatus(account, mine);
  if (!status) return null;
  const change = status.balance - account.size;
  const target = status.target;
  const toPass = target ? Math.max(0, target - status.balance) : null;
  const passPct = target ? Math.max(0, Math.min(100, ((status.balance - account.size) / (target - account.size)) * 100)) : null;
  const room = status.toBlow;
  const mll = account.maxLossLimit;
  const used = mll && room !== undefined ? Math.max(0, Math.min(100, (1 - room / mll) * 100)) : null;
  const C = 2 * Math.PI * 14;
  const cons = status.consistencyPct;
  return (
    <section className="dov-card dov-prop">
      <div>
        <span className="dov-label dov-cyan">{t(account.type === 'Funded' ? 'Funded account' : 'Prop evaluation')}</span>
        <strong className="dov-prop-balance">{money(status.balance)}</strong>
        <span className={tone(change)}>{signed(change)} ({pct((change / account.size) * 100)})</span>
        <span className="dov-muted">{t('{name} · started at {amount}', { name: account.name, amount: usd.format(account.size) })}</span>
      </div>
      <div className="dov-prop-bars">
        {toPass !== null && (
          <div>
            <span className="dov-row"><span>{t('Left to pass')}</span><b className="dov-cyan">{usd.format(toPass)}</b></span>
            <span className="dov-bar"><i className="cyan" style={{ width: `${passPct}%` }} /></span>
            <small className="dov-muted">{t('Target {amount}', { amount: usd.format(target) })}</small>
          </div>
        )}
        {used !== null && (
          <div>
            <span className="dov-row"><span>{t('Max-loss room left')}</span><b className={used >= 75 ? 'loss' : 'gold'}>{usd.format(Math.max(0, room))}</b></span>
            <span className="dov-bar"><i className={used >= 75 ? 'loss' : 'gold'} style={{ width: `${100 - used}%` }} /></span>
            <small className="dov-muted">{t('{pct}% of the room used', { pct: Math.round(used) })}</small>
          </div>
        )}
      </div>
      {account.consistencyLimit && cons !== null && cons !== undefined ? (
        <div className="dov-cons">
          <svg viewBox="0 0 36 36" aria-hidden="true"><circle cx="18" cy="18" r="14" /><circle cx="18" cy="18" r="14" className={status.consistencyOk ? 'on' : 'on bad'} strokeDasharray={`${Math.min(1, cons / 100) * C} ${C}`} /><text x="18" y="21">{Math.round(cons)}%</text></svg>
          <b>{t('Consistency')}</b>
          <small className="dov-muted">{t('Best day = {pct}% of profit · rule is {limit}% max', { pct: Math.round(cons), limit: account.consistencyLimit })}</small>
        </div>
      ) : <div />}
    </section>
  );
}

// One chart, three views: cumulative P&L, daily P&L, monthly P&L.
function Chart({ trades }) {
  const { t } = useI18n();
  const [view, setView] = useState('cumulative');
  const days = useMemo(() => dayNets(trades), [trades]);
  const last30 = days.slice(-30);
  // The running P&L across the window, from zero, so 30 days of movement fill the chart.
  let run = 0;
  const points = last30.map((d) => ({ key: d.key, v: (run += d.net) }));
  const windowNet = last30.reduce((s, d) => s + d.net, 0);
  const months = useMemo(() => {
    const out = {};
    for (const x of trades) out[tradingDayKey(x.date).slice(0, 7)] = (out[tradingDayKey(x.date).slice(0, 7)] || 0) + netPnl(x);
    return Object.entries(out).sort().slice(-12);
  }, [trades]);
  const W = 880;
  const H = 230;
  const lo = Math.min(0, ...points.map((p) => p.v));
  const hi = Math.max(0, ...points.map((p) => p.v));
  const y = (v) => 12 + (1 - (v - lo) / Math.max(1, hi - lo)) * (H - 24);
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${((i / Math.max(1, points.length - 1)) * W).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
  const mMax = Math.max(1, ...months.map(([, v]) => Math.abs(v)));
  return (
    <section className="dov-card dov-chart">
      <div className="dov-row dov-chart-head">
        <div>
          <strong className="dov-h">{t(view === 'monthly' ? 'Monthly P&L' : view === 'daily' ? 'Daily P&L' : 'Cumulative P&L')}</strong>
          <span className="dov-muted">{t(view === 'monthly' ? 'Your last 12 months' : 'Last 30 trading days')}</span>
          {view !== 'monthly' && <strong className={`dov-chart-net ${tone(windowNet)}`}>{signed(windowNet)}</strong>}
        </div>
        <div className="dov-seg" role="group" aria-label={t('Show by')}>
          {[['cumulative', 'Cumulative'], ['daily', 'Daily'], ['monthly', 'Monthly']].map(([id, label]) => (
            <button key={id} type="button" className={view === id ? 'on' : ''} aria-pressed={view === id} onClick={() => setView(id)}>{t(label)}</button>
          ))}
        </div>
      </div>
      {view === 'daily' ? <DailyBars trades={trades} /> : view === 'monthly' ? (
        <div className="dov-months">
          {months.map(([m, v]) => (
            <span key={m} title={`${monthShort(m)}: ${signed(v)}`}>
              <span className="dov-month-col"><i className={tone(v)} style={{ height: `${(Math.abs(v) / mMax) * 100}%` }} /></span>
              <b className={tone(v)}>{signed(v).replace('.00', '')}</b>
              <small>{monthShort(m)}</small>
            </span>
          ))}
        </div>
      ) : points.length > 1 ? (
        <>
          <svg className="dov-curve" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t('Cumulative P&L')}>
            <defs><linearGradient id="dov-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#1fd7ef" stopOpacity="0.35" /><stop offset="1" stopColor="#1fd7ef" stopOpacity="0" /></linearGradient></defs>
            <line x1="0" x2={W} y1={y(0)} y2={y(0)} className="zero" />
            <path d={`${path} L${W} ${H} L0 ${H} Z`} fill="url(#dov-fill)" />
            <path d={path} className="line" />
          </svg>
          <span className="dov-row dov-muted dov-axis"><small>{dayShort(points[0].key)}</small><small>{dayShort(points[points.length - 1].key)}</small></span>
        </>
      ) : <p className="dov-muted">{t('The chart fills in after a few trading days.')}</p>}
    </section>
  );
}

// The biggest leak right now: what it is, the comparison, what it cost, a recommendation and a
// rule to add to the trader's rules (the ones in Morning prep).
function Leak({ trades, library }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const insight = useMemo(() => topInsight(trades, library, true), [trades, library]);
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  async function addRule() {
    setBusy(true);
    try {
      await addRoutineRule(insight.rule);
      setAdded(true);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  }
  const real = insight && insight.kind !== 'none';
  return (
    <section className={`dov-card dov-leak${real ? '' : ' quiet'}`}>
      <span className="dov-chip">💡 {t(real ? 'Biggest leak right now' : 'Insight')}</span>
      {!insight ? <p className="dov-leak-text">{t('Log at least 10 trades in the last 30 days to unlock your first insight.')}</p> : (
        <>
          <p className="dov-leak-text">{insight.text}</p>
          {insight.compare && (
            <div className="dov-compare">
              <span>{insight.compare.label}</span><span className="dov-bar"><i className="loss" style={{ width: `${insight.compare.value}%` }} /></span><b className="loss">{insight.compare.value}%</b>
              <span>{insight.compare.otherLabel}</span><span className="dov-bar"><i className="cyan" style={{ width: `${insight.compare.other}%` }} /></span><b>{insight.compare.other}%</b>
            </div>
          )}
          {insight.tip && (
            <div className="dov-rec"><span className="dov-label dov-cyan">{t('Recommendation')}</span><p>{insight.tip}</p></div>
          )}
          {real && <span className="dov-sample">{insight.sample < 10 ? t('An early sign: based on {n} trades', { n: insight.sample }) : t('Based on {n} trades · enough to trust', { n: insight.sample })}</span>}
          <div className="dov-row dov-leak-actions">
            {real && insight.rule && (added
              ? <span className="gain">✓ {t('Added to your rules')}</span>
              : <button type="button" className="dov-gold" disabled={busy} onClick={addRule}>+ {t('Add as a rule')}</button>)}
            <button type="button" className="link-button" onClick={() => navigate('/app/insights')}>{t('See all insights')} &rarr;</button>
          </div>
          {real && insight.rule && !added && <small className="dov-muted">{t('Adds “{rule}” to your rules in Morning prep.', { rule: insight.rule })}</small>}
        </>
      )}
    </section>
  );
}

// Your trades, compact: best, worst or waiting for review, one trade per row; click to open it.
function TradeList({ trades, onEditTrade }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState('best');
  const byNet = [...trades].sort((a, b) => netPnl(b) - netPnl(a));
  const waiting = unjournaledTrades(trades);
  const lists = {
    best: byNet.filter((x) => netPnl(x) > 0).slice(0, 5),
    worst: byNet.filter((x) => netPnl(x) < 0).reverse().slice(0, 5),
    review: waiting.slice(0, 5),
  };
  const when = (x) => new Date(x.date).toLocaleString(currentLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return (
    <section className="dov-card dov-tradelist">
      <div className="dov-row"><strong className="dov-h">{t('Your trades')}</strong><button type="button" className="link-button" onClick={() => navigate('/app')}>{t('All trades')} &rarr;</button></div>
      <div className="dov-seg dov-tl-tabs" role="group" aria-label={t('Show')}>
        {[['best', 'Best'], ['worst', 'Worst'], ['review', 'Needs review']].map(([id, label]) => (
          <button key={id} type="button" className={tab === id ? 'on' : ''} aria-pressed={tab === id} onClick={() => setTab(id)}>
            {t(label)}{id === 'review' && waiting.length > 0 && <span className="dov-count">{waiting.length}</span>}
          </button>
        ))}
      </div>
      {lists[tab].length ? (
        <ul className="dov-tl">
          {lists[tab].map((x) => {
            const side = String(x.side || '').toLowerCase().startsWith('s') ? 'short' : 'long';
            return (
              <li key={x._id}>
                <button type="button" onClick={() => onEditTrade && onEditTrade(x)}>
                  <span><b>{x.symbol}</b> <span className={`dov-side ${side}`}>{t(side === 'short' ? 'Short' : 'Long')} ×{x.size}</span><small>{when(x)}</small></span>
                  <strong className={tone(netPnl(x))}>{signed(netPnl(x))}</strong>
                </button>
              </li>
            );
          })}
        </ul>
      ) : <p className="dov-muted">{t(tab === 'best' ? 'No winning trades yet.' : tab === 'worst' ? 'No losing trades yet.' : 'Every trade has tags or notes. Nice work.')}</p>}
    </section>
  );
}

export default function DashboardOverview({ ctx }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { trades, accounts, account, shownIds, allTrades, onSelectAccount } = ctx;
  const shown = accounts.filter((a) => !a.archived && (!shownIds || shownIds.includes(a._id)));
  const propPicked = account && isPropAccount(account) && account.size;
  const waiting = unjournaledTrades(trades).length;
  const latestDay = trades.length ? trades.map((x) => tradingDayKey(x.date)).sort().pop() : null;
  // Open positions from Interactive Brokers sync, shown on their account's card.
  const [positions, setPositions] = useState([]);
  useEffect(() => {
    let live = true;
    api('/ibkr/positions').then((d) => live && setPositions((d && d.positions) || [])).catch(() => {});
    return () => { live = false; };
  }, []);
  return (
    <div className="dov">
      <Numbers trades={trades} accounts={accounts} />
      <ScoreBar trades={trades} />

      {propPicked ? <PropPanel account={account} trades={allTrades} /> : shown.length > 0 && (
        <>
          <div className="dov-row dov-section-head"><span className="dov-label">{t('Accounts')}</span><button type="button" className="link-button" onClick={() => navigate('/app/accounts')}>{t('Manage accounts')} &rarr;</button></div>
          <div className="dov-accounts">
            {shown.map((a) => <AccountCard key={a._id} account={a} trades={allTrades} positions={positions.filter((p) => String(p.accountId) === String(a._id))} onOpen={() => onSelectAccount(a._id)} />)}
          </div>
        </>
      )}

      <div className="dov-pair">
        <Chart trades={trades} />
        <Leak trades={trades} library={ctx.library} />
      </div>

      <div className="dov-pair dov-bottom">
        <section className="dov-card dov-cal">
          <Calendar trades={trades} account={account} size={ctx.size} startDay={latestDay} insights={ctx.insights} library={ctx.library} notes={ctx.notes} setNotes={ctx.setNotes}
            onEditTrade={ctx.onEditTrade} onAddTrade={ctx.onAddTrade} onSessionExpired={ctx.onSessionExpired} />
        </section>
        <div className="dov-trades">
          <TradeList trades={trades} onEditTrade={ctx.onEditTrade} />
          {waiting > 0 && (
            <div className="dov-review">
              <span><b>{t('Needs review')}</b><small>{t(waiting === 1 ? '{n} trade waiting for a tag or a note' : '{n} trades waiting for a tag or a note', { n: waiting })}</small></span>
              <button type="button" className="small" onClick={() => navigate('/app')}>{t('Review now')} &rarr;</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
